/* eslint-disable no-underscore-dangle */
import Backbone from 'backbone';
import _ from 'underscore';

import { TerminalMonitoringResponseMapping } from './TerminalMonitoringResponseMapping';
import CCTerminal from 'app/src/data/models/CCTerminal';
import PaymentFactory from 'app/src/payments/PaymentFactory';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import ITerminal from 'app/src/payments/terminals/ITerminal';

import Decimal from '../../data/base/Decimal';
import Payment from '../../data/models/Payment';
import TerminalPayment from '../TerminalPayment';

const _TerminalStatus = {
  DISCONNECTED: 'Disconnected',
  ONLINE: 'Online',
  ERROR: 'Error',
  BUSY: 'Busy',
};

const _TerminalTransactionResponse = {
  CANCELLED: 'Cancelled',
  CANCELLED_FROM_TERMINAL: 'Cancelled from Terminal',
  CANCELLED_FROM_POS: 'Cancelled from the POS',
  CANCEL_UNAVAILABLE: 'Cancel Unavailable',
  APPROVED: 'Approved',
  APPROVED_WITH_ERROR: 'Approved with Error',
  DECLINED: 'Declined',
  TERMINAL_ERROR: 'Terminal Error',
  COULD_NOT_START_TERMINAL: 'Could not Start Terminal Transaction',
  TIMEOUT: 'Timed Out',
  ERROR: 'Error',
  PAYMENT_ALREADY_RECONCILED: 'Payment Completed',
  PRE_AUTHORIZED: 'Payment Pre-Authorized',
  SERVICE_EXCEPTION: 'Service Exception',
  UNKNOWN: 'Unknown',
  NON_CANCELLABLE_STATE: 'The payment cannot be cancelled',
} as const;

type TerminalTransactionResponseKey = keyof typeof _TerminalTransactionResponse;

type TerminalTransactionResponseType =
  (typeof _TerminalTransactionResponse)[TerminalTransactionResponseKey];

const _TerminalEvent = {
  PAYMENT_STARTED: 'Payment Started', // when we receive token from payment service, notifying us that we can cancel the transaction if we need to
  CANCEL_STARTED: 'Cancel Started', // Used to notify terminal when cancel action began
};

/**
 * Internal class used to present a uniform terminal response.
 */
export class TerminalTransactionResponse {
  _response: TerminalTransactionResponseType;
  _status: string | null;
  _payment: TerminalPayment | null;
  _error: string | null;

  constructor(
    response: TerminalTransactionResponseType,
    status: string | null = null,
    payment: TerminalPayment | null = null,
    error: string | null = null
  ) {
    this._response = response;
    this._status = status;
    this._payment = payment;
    this._error = error;
  }

  get response() {
    return this._response;
  }

  set response(response) {
    this._response = response;
  }

  /**
   * Getter for the transaction status.  This would typically be the raw
   * status as reported by the terminal.
   *
   * @returns {string}    The transaction status as reported by the terminal.
   */
  get status() {
    return this._status;
  }

  /**
   * Getter for the terminal payment object.  This may not be available depending on where
   * a transaction fails.
   *
   * @returns {TerminalPayment|null}
   */
  get payment() {
    return this._payment;
  }

  /**
   * Getter for the error reported from the terminal or generated by the concrete terminal
   * object.  This would typically be used by a view controller to display an appropriate
   * error message to the user.
   *
   * @returns {string}    The error message
   */
  get error() {
    return this._error;
  }

  get terminalMonitoringStatus() {
    const statusKey = Object.keys(_TerminalTransactionResponse).find(
      (key) => _TerminalTransactionResponse[key as TerminalTransactionResponseKey] === this.response
    );

    return TerminalMonitoringResponseMapping[statusKey as TerminalTransactionResponseKey];
  }

  get responseCode() {
    return Object.keys(_TerminalTransactionResponse).find(
      (key) => _TerminalTransactionResponse[key as TerminalTransactionResponseKey] === this.response
    );
  }
}

/**
 * Base implementation of a payment terminal.
 */
class Terminal extends ITerminal {
  _model: CCTerminal;
  _usesProprietaryUI: boolean;
  _lastPayment: Payment | null;
  /**
   * Constructor for a Terminal object.  Throws an error if an invalid
   * model is specified.
   */
  constructor(model: CCTerminal) {
    super();
    if (model instanceof CCTerminal) {
      this._model = model;
      this._usesProprietaryUI = false;
      this._lastPayment = null;
    } else {
      // eslint-disable-next-line no-throw-literal
      throw 'A terminal requires a terminal model on creation.';
    }
  }

  /**
   * Exports the Status enum through the Terminal class.
   */
  static get Status() {
    return _TerminalStatus;
  }

  /**
   * Exports the Response enum through the Terminal class.
   */
  static get Response() {
    return _TerminalTransactionResponse;
  }

  /**
   * Exports for all custom window events, Terminal Modal can subscribe.
   * @returns {_TerminalEvent}
   */
  static get TerminalEvents() {
    return _TerminalEvent;
  }

  /**
   * Getter for the contained API terminal model.
   *
   * @returns {CCTerminal}    The API model representation of the terminal
   */
  get model() {
    return this._model;
  }

  /**
   * Retrieves the usesProprietaryUI property of the terminal.  This is set for any concrete classes
   * that utilize their own UI for processing transactions.
   *
   * @returns {boolean}   True if the terminal processes transactions with a proprietary UI; if false,
   *                      the terminal will use the Retail UI
   */
  get usesProprietaryUI() {
    return this._usesProprietaryUI;
  }

  /**
   * Getter for the last payment initiated by the terminal.  This is an in-memory cache of the last
   * transaction.  No attempt is made to retrieve any prior transaction from the API.
   *
   * @returns {Payment|null}  The last payment transacted with the terminal
   */
  get lastPayment() {
    return this._lastPayment;
  }

  /**
   * Setter for the last payment initiated by the terminal.  This is an in-memory cache of the last
   * transaction.  No attempt is made to retrieve any prior transactions from the API.
   *
   * @param {Payment|null} payment     The payment
   */
  set lastPayment(payment) {
    this._lastPayment = payment;
  }

  /**
   * Retrieve information about the terminal.
   *
   * @returns {{hardwareModel: *, name: *, terminalID: *}}
   */
  info() {
    return {
      hardwareModel: this.description(),
      name: this.model.get('name'),
      terminalID: this.model.id,
    };
  }

  /**
   * Whether tipping is supported on the terminal. Overridden in relevant concrete implementations.
   *
   * @returns {boolean}
   */
  supportsTipping() {
    return false;
  }

  /**
   * Creates a terminal payment given the amount and the charge id.
   *
   * @param amount {Decimal}  The payment amount
   * @param ccChargeID {Number}   The ID of the charge
   * @param rawResponse {String}  The raw response received from the terminal from a transaction request
   * @param saleID {Number}   The ID of the charge
   * @param tipAmount {Decimal}  The payment tipAMount
   * @returns {TerminalPayment}   The terminal payment
   * @protected
   */
  _createPayment(
    amount: Decimal,
    ccChargeID = null,
    rawResponse = null,
    saleID = 0,
    tipAmount = null
  ) {
    return PaymentFactory.createPayment(
      PaymentTypeManager.Types.TERMINAL,
      amount,
      ccChargeID,
      rawResponse,
      saleID,
      tipAmount
    );
  }
}

// Backbone currently defines Backbone.Events as an object preventing us from using the ES6
// syntax to extend from it.  So, instead, we'll do it the old-fashioned way and attach it
// to the prototype.
_.extend(Terminal.prototype, Backbone.Events);

export default Terminal;
